import { ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useSoteriaSidePanelStore } from '../stores/sidePanelStore';
import { SoteriaCard, soteriaCardCtaClass } from './SoteriaCard';
import type { CreatedStrategy } from '../hooks/useSoteriaChat';

/**
 * StrategyCreatedCard
 * -------------------
 * Shown when Soteria saves a strategy to the workspace.
 *
 * "Open" goes to the strategy, not to the list it sits in. It used to land on
 * /strategies and leave the advisor to find the thing they had just watched
 * being built. A fresh strategy has no results yet, so the report opens on its
 * Model Configuration tier (`?tier=config`) — the one tier with something on it.
 *
 * Closes the drawer on the way, like NavigateCard: the report is a full-viewport
 * `fixed inset-0` overlay, so leaving the assistant open over it stacks two
 * things that each expect the whole screen.
 *
 * Turns stored before the deep link carry the bare `/strategies` url, so the
 * search param is only attached to a report path — the list route takes none.
 */
export function StrategyCreatedCard({ strategy }: { strategy: CreatedStrategy }) {
  const navigate = useNavigate();
  const closePanel = useSoteriaSidePanelStore((s) => s.close);
  const isReport = strategy.url.includes('/report');

  const open = () => {
    closePanel();
    void navigate({
      to: strategy.url,
      ...(isReport ? { search: { tier: 'config' } } : {}),
    });
  };

  return (
    <SoteriaCard
      variant="default"
      icon={<Sparkles className="h-4 w-4" />}
      eyebrow="Strategy created in your workspace"
      title={strategy.name}
      subtitle={`Code: ${strategy.code}`}
      cta={
        <button
          type="button"
          onClick={open}
          className={soteriaCardCtaClass('default')}
        >
          Open
          <ArrowRight className="h-3 w-3" />
        </button>
      }
    />
  );
}
